import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { 
  checkIOCComplete, 
  checkDemographicsComplete,
  getMissingIOCFields,
  getMissingDemographicFields,
} from './validation-checks';

export interface PrePaymentRequirement {
  requirementName: string;
  status: boolean;
  fixUrl?: string;
  fixLabel?: string;
  missingFields?: string[];
}

export interface PrePaymentValidationParams {
  encounter: PatientEncounter | null;
  patient: PatientViewModel | null;
  patientId: number;
  encounterId: number;
  isLoading?: boolean;
}

export interface PrePaymentValidationResult {
  isValid: boolean;
  requirements: PrePaymentRequirement[];
  failedRequirements: PrePaymentRequirement[];
}

export const validatePrePaymentRequirements = (
  params: PrePaymentValidationParams
): PrePaymentValidationResult => {
  const {
    encounter,
    patient,
    patientId,
    encounterId,
    isLoading = false,
  } = params;

  const specialistDashboardUrl = `/specialist/dashboard/${patientId}`;
  const specialistDashboardWithIocUrl = `/specialist/dashboard/${patientId}?encounter=${encounterId}`;

  const demographicsComplete = !isLoading && checkDemographicsComplete(patient);
  const iocComplete = !isLoading && checkIOCComplete(encounter);

  const requirements: PrePaymentRequirement[] = [
    {
      requirementName: 'Patient demographic information is incomplete',
      status: demographicsComplete,
      fixUrl: specialistDashboardUrl,
      fixLabel: 'Complete Patient Demographics',
      missingFields: !demographicsComplete ? getMissingDemographicFields(patient) : undefined,
    },
    {
      requirementName: 'Instance of care is incomplete',
      status: iocComplete,
      fixUrl: specialistDashboardWithIocUrl,
      fixLabel: 'Complete Instance of Care',
      missingFields: !iocComplete ? getMissingIOCFields(encounter) : undefined,
    },
  ];

  // Add loading requirement if still loading
  if (isLoading) {
    requirements.unshift({
      requirementName: 'IOC and Patient data have not fully loaded',
      status: false,
    });
  }

  const failedRequirements = requirements.filter((req) => !req.status);
  const isValid = failedRequirements.length === 0;

  return {
    isValid,
    requirements,
    failedRequirements,
  };
};

/**
 * Quick check to see if all pre-payment requirements are met.
 */
export const arePrePaymentRequirementsMet = (
  params: PrePaymentValidationParams
): boolean => {
  return validatePrePaymentRequirements(params).isValid;
};
